import { ApiReceivingLogRow } from "./ApiReceivingLogRow";
import { EmailData } from "../email/EmailData";
import { ReceiptSubmitRow } from "./ReceiptSubmitRow";
import { MissingItem } from "./MissingItem";
import { AiPoFeedback } from "./AiPoFeedback";
import { ContainerItem } from "./ContainerItem";
/**
 * Request for adding a new receiving log with optional email data
 */
export class AddReceivingLogRequest {
    public constructor(
        receivingLogRow: ApiReceivingLogRow,
        emailData?: EmailData,
        photoBlobNames?: string[],
        receipts?: ReceiptSubmitRow[],
        completesPo?: boolean | null,
        containerItems?: ContainerItem[],
        missingItems?: MissingItem[],
        expeditingEmailData?: EmailData
      ) {
        this.receivingLogRow = receivingLogRow;
        this.emailData = emailData;
        this.photoBlobNames = photoBlobNames;
        this.receipts = receipts;
        this.completesPo = completesPo;
        this.containerItems = containerItems;
        this.missingItems = missingItems;
        this.expeditingEmailData = expeditingEmailData;
      }

    /**
     * The receiving log row data to be saved
     */
    receivingLogRow: ApiReceivingLogRow;

    /**
     * Optional email message data if an email should be sent
     */
    emailData?: EmailData;

    /**
     * Optional photos blob names to be included in the email
     */
    photoBlobNames?: string[];

    /**
     * Structured ledger rows for this delivery. Omitted/empty = no ledger writes.
     */
    receipts?: ReceiptSubmitRow[];

    /**
     * PO-level manual "done" decision. `true`/`false` = explicit user decision;
     * `null`/omit = no decision made, leave PO status untouched.
     */
    completesPo?: boolean | null;

    /**
     * The physical containers on this delivery, as (quantity, type) pairs —
     * "3 box, 2 pallet". The backend creates one MM.ReceivingItems row per unit
     * of each pair, numbers the labels within the pair (BOX 1 of 3 … PALLET 2 of
     * 2), and returns the rows on `receivingItems`. It also DERIVES
     * `receivingLogRow.quantity` from this, overwriting whatever was sent.
     *
     * A type must not repeat ("3 box, 2 box" is a 400 — two stickers would both
     * claim to be "BOX 1"). Omit for legacy submissions (no item rows).
     *
     * Mutually exclusive with `itemQuantity`/`itemType`: sending BOTH shapes is
     * rejected with a 400 unless the scalars exactly restate a single pair.
     */
    containerItems?: ContainerItem[];

    /**
     * LEGACY single-type quantity, superseded by `containerItems`. Still accepted
     * on its own (byte-identical to the old behavior) and used by the desktop log
     * form, which parses one pair out of its free-text "Quantity Units" field.
     * Never send these alongside `containerItems`.
     */
    itemQuantity?: number | null;

    /** LEGACY single-type unit noun — see {@link itemQuantity}. */
    itemType?: string | null;

    /**
     * The outstanding items on this delivery (plain shortfalls + refused/returned
     * goods). Drives the expediting / missing-items email body and folds into the
     * GP note context. Empty/omit when nothing is outstanding.
     */
    missingItems?: MissingItem[];

    /**
     * Config for the SEPARATE expediting / missing-items email (its own recipients
     * and include-photos choice). When present, the backend sends the expediting
     * email (subject/body rendered server-side from `missingItems`, damage photos
     * attached from the `DamagePhotos` multipart field) and reports the outcome on
     * `AddReceivingLogResponse.expeditingEmailStatus`. Present only when there are
     * missing items and the expediting email is left on.
     */
    expeditingEmailData?: EmailData;

    /**
     * Idempotency key (a client-minted GUID) for this submission. The backend
     * dedupes on it so a retried POST (e.g. after a network timeout) returns the
     * original log instead of creating a duplicate. Assigned by the caller after
     * construction; also reused as the IndexedDB key for the held photo bytes.
     */
    submissionKey?: string;

    /**
     * A packing-slip AI PO-extraction error, set only when the user reported one:
     * either the AI's detected PO was wrong and they corrected it (`wrongPo`), or
     * the AI found no PO and they confirmed one was on the slip (`missedPo`). The
     * backend records it to MM.ReceivingAiFeedbackEvents with the new log ID and
     * the log's PO as the correct value. Assigned by the caller after
     * construction; omitted when the AI was right or no AI ran.
     */
    aiPoFeedback?: AiPoFeedback;


  }
